import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiResponse } from '@/lib/api-client';
import type {
  CaseChannel,
  CaseStatus,
  ClaimCategory,
  CompletenessSummary,
  DocumentValidationStatus,
  CaseFlow,
  FlowStep,
  TravelClaimType,
} from '@tci/shared-types';

// Query keys factory (mirrors claimKeys in use-claims.ts)
export const caseKeys = {
  all: ['cases'] as const,
  lists: () => [...caseKeys.all, 'list'] as const,
  list: (filters: CaseFilters) => [...caseKeys.lists(), filters] as const,
  details: () => [...caseKeys.all, 'detail'] as const,
  detail: (id: string) => [...caseKeys.details(), id] as const,
  flow: (id: string) => [...caseKeys.detail(id), 'flow'] as const,
  policies: (search: string) => ['policies', 'list', search] as const,
};

export interface CaseFilters {
  status?: CaseStatus | string;
  travelClaimType?: TravelClaimType | string;
  channel?: CaseChannel | string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CaseDocumentSummary {
  id: string;
  documentType: string;
  fileName: string;
  /** Resolved server-side; decides whether the viewer can render it in place. */
  mimeType?: string | null;
  sizeBytes?: number | null;
  stepId?: string | null;
  validationStatus: DocumentValidationStatus | string;
  validationNote?: string | null;
  createdAt: string;
  /** Set when a later upload replaced this one. Kept, never deleted (PD 12.8). */
  supersededAt?: string | null;
}

export interface CaseSummary {
  id: string;
  caseNumber: string;
  status: CaseStatus | string;
  channel: CaseChannel | string;
  /** The line of business. Travel refines it further with `travelClaimType`. */
  category: ClaimCategory | string;
  travelClaimType?: TravelClaimType | string | null;
  claimant?: { id: string; fullName?: string | null; phoneNumber: string } | null;
  /** Name supplied during intake; it may not have been identity-verified. */
  statedClaimantName?: string | null;
  policy?: { id: string; policyNumber: string; insuredName?: string } | null;
  policyNumberRaw?: string | null;
  needsPolicyReview: boolean;
  destination?: string | null;
  incidentDate?: string | null;
  notifiedLate: boolean;
  outOfWindow: boolean;
  reviewNote?: string | null;
  submittedAt?: string | null;
  createdAt: string;
  answers: Record<string, string | number | boolean>;
  currentStep?: FlowStep | null;
  completeness?: CompletenessSummary | null;
  bankName?: string | null;
  /** Last 4 digits only. The full number is encrypted at rest and returned
   *  solely by the audited reveal endpoint. */
  bankAccountLast4?: string | null;
  bankAccountHolderName?: string | null;
  documents?: CaseDocumentSummary[];
  evidenceRequirements?: Array<{
    documentType: string;
    isMandatory: boolean;
    description?: string | null;
  }>;
  convertedClaim?: { id: string; claimNumber: string; status?: string } | null;
  sourceMeta?: Record<string, unknown> | null;
}

export interface CaseListResponse {
  cases: CaseSummary[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
  statusBreakdown: Record<string, number>;
}

export interface PolicySummary {
  id: string;
  policyNumber: string;
  insuredName: string;
  insuredPhone?: string | null;
  planTier?: string | null;
  destination?: string | null;
  tripStartDate?: string | null;
  tripEndDate?: string | null;
  tenant?: { id: string; name: string };
}

export function useCases(filters: CaseFilters = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: caseKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', String(filters.status));
      if (filters.travelClaimType)
        params.append('travelClaimType', String(filters.travelClaimType));
      if (filters.channel) params.append('channel', String(filters.channel));
      if (filters.search) params.append('search', filters.search);
      if (filters.page) params.append('page', String(filters.page));
      if (filters.limit) params.append('limit', String(filters.limit));
      const { data } = await apiClient.get<ApiResponse<CaseListResponse>>(
        `/cases?${params.toString()}`
      );
      return data.data;
    },
    staleTime: 30 * 1000,
    // The sidebar badge shares this query; gated by the caller's permissions.
    enabled: options?.enabled ?? true,
  });
}

export function useCase(caseId: string) {
  return useQuery({
    queryKey: caseKeys.detail(caseId),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<CaseSummary>>(`/cases/${caseId}`);
      return data.data;
    },
    enabled: !!caseId,
  });
}

/**
 * The flow version this case is pinned to, as the server walks it.
 *
 * Fetched rather than taken from the built-in `CASE_FLOWS`: a document upload
 * is filed against a step id, and the submit guard checks the *pinned* flow's
 * steps — a step id read from a different version would answer nothing.
 */
export function useCaseFlow(caseId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: caseKeys.flow(caseId),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<CaseFlow>>(`/cases/${caseId}/flow`);
      return data.data;
    },
    enabled: !!caseId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Upload one piece of evidence against a document step, then attach it.
 *
 * Two calls, the same pair the agent's assisted intake makes: the upload files
 * the bytes against the step (retiring any earlier file there), and the
 * correction records the stored id as the step's answer. The second call is
 * what the submit guard reads — an upload alone ticks the type-based checklist
 * but leaves the step unanswered, which is how a case showing 3/3 mandatory
 * uploaded was refused with all three named as missing.
 *
 * The corrections endpoint rather than `PATCH :id/answers`, so the attachment
 * is audited as a staff action and a claimant mid-conversation is not moved.
 */
export function useUploadStepDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      caseId,
      step,
      file,
    }: {
      caseId: string;
      step: FlowStep;
      file: File;
    }) => {
      const formData = new FormData();
      // Fields before the file: Fastify reads multipart parts in order, and
      // fields after the file are not populated when the service reads them.
      formData.append('type', String(step.documentType ?? 'OTHER_DOCUMENT'));
      formData.append('stepId', step.id);
      formData.append('file', file);
      const { data: uploaded } = await apiClient.post<ApiResponse<{ id: string }>>(
        `/cases/${caseId}/documents/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const { data: attached } = await apiClient.patch<
        ApiResponse<{ accepted: boolean; error?: string }>
      >(`/cases/${caseId}/corrections`, { stepId: step.id, value: uploaded.data.id });
      return attached.data;
    },
    onSettled: (_result, _error, { caseId }) =>
      queryClient.invalidateQueries({ queryKey: caseKeys.detail(caseId) }),
  });
}

export interface CreateCaseInput {
  travelClaimType: TravelClaimType | string;
  channel?: CaseChannel | string;
  claimantPhone?: string;
  claimantFullName?: string;
  claimantNric?: string;
  /**
   * Set once the claimant has been resolved, which the capture form now does
   * before creating the case — consent is recorded against a claimant, and
   * `create` refuses without a live one. Supplying the id skips the gateway's
   * own findOrCreate, so the record consent was attached to is provably the
   * record the case is opened for.
   */
  claimantId?: string;
  answers?: Record<string, string | number | boolean>;
  sourceMeta?: Record<string, unknown>;
}

export function useCreateCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCaseInput) => {
      const { data } = await apiClient.post<ApiResponse<CaseSummary>>('/cases', input);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: caseKeys.lists() }),
  });
}

export function useSubmitCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (caseId: string) => {
      const { data } = await apiClient.post<ApiResponse<CaseSummary>>(`/cases/${caseId}/submit`);
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: caseKeys.all }),
  });
}

/**
 * A staff correction to one intake answer — MASTER_PLAN §6 item 21.
 *
 * Deliberately not the conversational `PATCH :id/answers`: the corrections
 * endpoint is staff-only, audited with before/after values, and leaves the
 * claimant's conversational cursor where it was. The server returns
 * `accepted: false` with the flow's own validation message for a bad value,
 * so the caller surfaces that rather than a thrown error.
 */
export function useCorrectAnswer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      caseId,
      stepId,
      value,
    }: {
      caseId: string;
      stepId: string;
      value: string | number | boolean;
    }) => {
      const { data } = await apiClient.patch<
        ApiResponse<{ accepted: boolean; error?: string }>
      >(`/cases/${caseId}/corrections`, { stepId, value });
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: caseKeys.all }),
  });
}

function useCaseAction(action: 'request-info' | 'refer-expert' | 'reject' | 'abandon') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, note }: { caseId: string; note: string }) => {
      const { data } = await apiClient.post<ApiResponse<CaseSummary>>(
        `/cases/${caseId}/${action}`,
        { note }
      );
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: caseKeys.all }),
  });
}

export interface ExpertReferral {
  id: string;
  question: string;
  expertName: string | null;
  referredAt: string;
  outcome: 'PROCEED' | 'DECLINE' | null;
  opinion: string | null;
  outcomeAt: string | null;
}

/** Every expert instruction on a case — what was asked, and what came back. */
export function useExpertReferrals(caseId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...caseKeys.detail(caseId), 'expert-referrals'] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<ExpertReferral[]>>(
        `/cases/${caseId}/expert-referrals`
      );
      return data.data;
    },
    enabled: (options?.enabled ?? true) && !!caseId,
  });
}

/** Record what the expert answered. Once per referral — the server enforces it. */
export function useRecordExpertOutcome() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      caseId,
      outcome,
      opinion,
    }: {
      caseId: string;
      outcome: 'PROCEED' | 'DECLINE';
      opinion: string;
    }) => {
      const { data } = await apiClient.post<ApiResponse<ExpertReferral>>(
        `/cases/${caseId}/expert-outcome`,
        { outcome, opinion }
      );
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: caseKeys.all }),
  });
}

export const useRequestCaseInfo = () => useCaseAction('request-info');
export const useReferCaseToExpert = () => useCaseAction('refer-expert');
export const useRejectCase = () => useCaseAction('reject');
export const useAbandonCase = () => useCaseAction('abandon');

export function useConvertCase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (caseId: string) => {
      const { data } = await apiClient.post<ApiResponse<CaseSummary>>(`/cases/${caseId}/convert`);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: caseKeys.all });
      queryClient.invalidateQueries({ queryKey: ['claims'] });
    },
  });
}

export function useLinkCasePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ caseId, policyId }: { caseId: string; policyId: string }) => {
      const { data } = await apiClient.post<ApiResponse<CaseSummary>>(
        `/cases/${caseId}/link-policy`,
        { policyId }
      );
      return data.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: caseKeys.all }),
  });
}

export function usePolicySearch(search: string) {
  return useQuery({
    queryKey: caseKeys.policies(search),
    queryFn: async () => {
      const { data } = await apiClient.get<
        ApiResponse<{ policies: PolicySummary[]; pagination: unknown }>
      >(`/policies?search=${encodeURIComponent(search)}`);
      return data.data.policies;
    },
    enabled: search.trim().length >= 2,
    staleTime: 60 * 1000,
  });
}
